import { useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { FormModal } from './FormModal';
import { SelectInput, TextInput } from '../inputs';
import {
  addResource,
  systemSelectors,
  useDispatch,
  useSelector,
} from '../../../state';
import { useRefresh } from '../../../hooks';
import {
  CustomerWarehouseAPI,
  MinimumCustomerMonthlySpendType,
} from '../../../types';

interface FormValues {
  minimumCustomerMonthlySpendAmount: string;
  minimumCustomerMonthlySpendType: MinimumCustomerMonthlySpendType | null;
  minimumCustomerMonthlyHandlingSpendAmount: string;
  minimumCustomerMonthlyStorageSpendAmount: string;
}

interface Props {
  open: boolean;
  handleClose: () => any;
  customerWarehouse: CustomerWarehouseAPI;
}

export function CustomerWarehouseSettingsForm({
  open,
  handleClose,
  customerWarehouse,
}: Props) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const refresh = useRefresh();

  const minimumCustomerMonthlySpendTypes = useSelector(
    systemSelectors.minimumCustomerMonthlySpendTypes,
  );

  const [submitting, setSubmitting] = useState(false);

  const validationSchema = useMemo(
    () =>
      object().shape({
        minimumCustomerMonthlySpendAmount: string().when(
          'minimumCustomerMonthlySpendType',
          {
            is: (val: MinimumCustomerMonthlySpendType | null) =>
              !!val && val !== 'HandlingAndStorage',
            then: (schema) =>
              schema.required('Minimum spend amount is required'),
          },
        ),
        minimumCustomerMonthlyHandlingSpendAmount: string().when(
          'minimumCustomerMonthlySpendType',
          {
            is: 'HandlingAndStorage' as MinimumCustomerMonthlySpendType,
            then: (schema) =>
              schema.required('Handling spend amount is required'),
          },
        ),
        minimumCustomerMonthlyStorageSpendAmount: string().when(
          'minimumCustomerMonthlySpendType',
          {
            is: 'HandlingAndStorage' as MinimumCustomerMonthlySpendType,
            then: (schema) =>
              schema.required('Storage spend amount is required'),
          },
        ),
      }),
    [],
  );

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitting(true);

      const { error } = await dispatch(
        addResource({
          baseUrl: `/customers/${customerWarehouse.customerID}/warehouse/${customerWarehouse.warehouseID}/warehouse-settings`,
          payload: {
            customerId: customerWarehouse.customerID,
            warehouseId: customerWarehouse.warehouseID,
            ...values,
          },
          message: 'Changes saved',
        }),
      );
      setSubmitting(false);
      if (!error) {
        refresh();
        handleClose();
      }
    },
    [
      dispatch,
      handleClose,
      refresh,
      customerWarehouse.customerID,
      customerWarehouse.warehouseID,
    ],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      minimumCustomerMonthlySpendAmount:
        customerWarehouse.minimumCustomerMonthlySpendAmount?.toString() || '',
      minimumCustomerMonthlySpendType:
        customerWarehouse.minimumCustomerMonthlySpendType || null,
      minimumCustomerMonthlyHandlingSpendAmount:
        (
          customerWarehouse.minimumCustomerMonthlyHandlingSpendAmount ??
          customerWarehouse.minimumCustomerMonthlySpendAmount
        )?.toString() || '',
      minimumCustomerMonthlyStorageSpendAmount:
        (
          customerWarehouse.minimumCustomerMonthlyStorageSpendAmount ??
          customerWarehouse.minimumCustomerMonthlySpendAmount
        )?.toString() || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const isSeparate =
    formik.values.minimumCustomerMonthlySpendType === 'HandlingAndStorage';

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title='Customer warehouse settings'
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      paperProps={{ style: { width: 468 } }}
      submitting={submitting}
    >
      <SelectInput
        name='minimumCustomerMonthlySpendType'
        formikProps={formik}
        label='Minimum monthly spend type'
        className={classes.input}
        options={minimumCustomerMonthlySpendTypes}
      />
      {isSeparate ? (
        <>
          <TextInput
            name='minimumCustomerMonthlyHandlingSpendAmount'
            formikProps={formik}
            label='Minimum monthly handling spend'
            className={classes.input}
            format='currency'
          />
          <TextInput
            name='minimumCustomerMonthlyStorageSpendAmount'
            formikProps={formik}
            label='Minimum monthly storage spend'
            className={classes.input}
            format='currency'
          />
        </>
      ) : (
        <TextInput
          name='minimumCustomerMonthlySpendAmount'
          formikProps={formik}
          label='Minimum monthly spend'
          className={classes.input}
          format='currency'
        />
      )}
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    marginBottom: 16,
    width: '100%',
  },
}));
